import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import FormField, { ErrorSummary } from '../common/FormField';
import { Plus, Edit2, Trash2, Upload } from 'lucide-react';
import ImportModal from '../common/ImportModal';
import { validators, validateForm } from '../../utils/validation';

const GROUP_NAMES = ['Asset', 'Liability', 'Income', 'Expense', 'Equity'];
// Each group gets its own 1000-series; codes start at base + 1 (Asset 1001, …).
const GROUP_CODE_BASE = { Asset: 1000, Liability: 2000, Income: 3000, Expense: 4000, Equity: 5000 };

const emptyGroup = { accountCode: '', accountName: '', groupName: 'Asset', parentId: '', displayOrder: 0 };

export default function AccountGroupMaster() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState(emptyGroup);
  const [editing, setEditing] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const groups = useMemo(() => state.accountGroups || [], [state.accountGroups]);
  const nameById = (id) => groups.find(g => g.id === id)?.accountName || '-';

  // Next free code in the group's 1000-series (e.g. Asset → 1001, 1002, …).
  const nextCodeFor = (groupName) => {
    const base = GROUP_CODE_BASE[groupName];
    if (!base) return '';
    const used = groups
      .filter(g => g.id !== editing)
      .map(g => Number(g.accountCode))
      .filter(n => !isNaN(n) && n > base && n < base + 1000);
    return String(used.length ? Math.max(...used) + 1 : base + 1);
  };

  const existingCodes = useMemo(() => groups.filter(g => g.accountCode).map(g => ({ id: g.id, value: g.accountCode })), [groups]);

  const errors = useMemo(() => {
    const { errors } = validateForm({
      accountCode: [
        validators.required(form.accountCode, 'Account code'),
        validators.unique(form.accountCode, existingCodes, 'Account code', editing),
      ],
      accountName: [validators.required(form.accountName, 'Account name')],
      displayOrder: [validators.positiveNumber(form.displayOrder, 'Display order')],
    });
    return errors;
  }, [form, existingCodes, editing]);

  const hasErrors = Object.values(errors).some(e => e);
  const touch = (f) => setTouched(p => ({ ...p, [f]: true }));

  const handleSave = () => {
    setSubmitted(true);
    setTouched({ accountCode: true, accountName: true, displayOrder: true });
    if (hasErrors) return;
    const payload = { ...form, displayOrder: Number(form.displayOrder) || 0 };
    if (editing) {
      dispatch({ type: 'UPDATE_ACCOUNT_GROUP', payload: { ...payload, id: editing } });
      addToast('Account group updated', 'success');
    } else {
      dispatch({ type: 'ADD_ACCOUNT_GROUP', payload });
      addToast('Account group added', 'success');
    }
    close();
  };

  const close = () => { setShowForm(false); setForm(emptyGroup); setEditing(null); setTouched({}); setSubmitted(false); };
  const openNew = () => { setForm({ ...emptyGroup, accountCode: nextCodeFor(emptyGroup.groupName) }); setEditing(null); setTouched({}); setSubmitted(false); setShowForm(true); };
  const openEdit = (row) => {
    setForm({ accountCode: row.accountCode || '', accountName: row.accountName || '', groupName: row.groupName || 'Asset', parentId: row.parentId || '', displayOrder: row.displayOrder || 0 });
    setEditing(row.id); setTouched({}); setSubmitted(false); setShowForm(true);
  };
  const handleDelete = async (row) => {
    const childCount = groups.filter(g => g.parentId === row.id).length;
    if (childCount > 0) { alert(`Cannot delete: ${childCount} account group(s) use this as their parent.`); return; }
    const ok = await confirm(`Delete account group "${row.accountName}"?`, { title: 'Delete Account Group', variant: 'danger', confirmLabel: 'Delete' });
    if (ok) { dispatch({ type: 'DELETE_ACCOUNT_GROUP', payload: row.id }); addToast('Account group deleted', 'success'); }
  };

  const sorted = useMemo(
    () => [...groups].sort((a, b) => (Number(a.displayOrder) || 0) - (Number(b.displayOrder) || 0) || (a.accountName || '').localeCompare(b.accountName || '')),
    [groups]
  );

  const columns = [
    { key: 'accountCode', label: 'Account Code', sortable: true },
    { key: 'accountName', label: 'Account Name', sortable: true },
    { key: 'groupName', label: 'Account Group Name', sortable: true, render: v => <span className="badge badge-gray">{v || '-'}</span> },
    { key: 'parentId', label: 'Parent Account', render: v => v ? nameById(v) : '-', exportValue: r => r.parentId ? nameById(r.parentId) : '' },
    { key: 'displayOrder', label: 'Display Order', align: 'right', sortable: true },
  ];

  // Parent options exclude self (and, when editing, its own descendants would ideally be excluded — kept simple to direct self only)
  const parentOptions = groups.filter(g => g.id !== editing);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Account Group</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)} className="btn btn-secondary flex items-center gap-2"><Upload size={16} /> Import</button>
          <button onClick={openNew} className="btn btn-primary flex items-center gap-2"><Plus size={16} /> Add Account Group</button>
        </div>
      </div>

      <ImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        title="Import Account Groups"
        dedupeKey="accountCode"
        existing={groups.map(g => g.accountCode)}
        columns={[
          { key: 'accountCode', label: 'Account Code', required: true },
          { key: 'accountName', label: 'Account Name', required: true },
          { key: 'groupName', label: 'Account Group Name' },
          { key: 'displayOrder', label: 'Display Order', type: 'number' },
        ]}
        sample={{ accountCode: '1001', accountName: 'Current Assets', groupName: 'Asset', displayOrder: 1 }}
        onAdd={(p) => dispatch({ type: 'ADD_ACCOUNT_GROUP', payload: { ...emptyGroup, ...p, groupName: p.groupName || 'Asset', displayOrder: Number(p.displayOrder) || 0 } })}
      />

      <div className="card">
        <DataTable
          columns={columns}
          data={sorted}
          searchFields={['accountCode', 'accountName', 'groupName']}
          exportFilename="account-groups"
          emptyState={{ title: 'No account groups yet', description: 'Build your chart-of-accounts groups (with parent grouping and display order).', actionLabel: 'Add Account Group', onAction: openNew }}
          actions={(row) => (
            <>
              <button onClick={() => openEdit(row)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Edit"><Edit2 size={15} /></button>
              <button onClick={() => handleDelete(row)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>

      <Modal isOpen={showForm} onClose={close} title={editing ? 'Edit Account Group' : 'Add Account Group'} size="lg">
        <ErrorSummary errors={errors} show={submitted && hasErrors} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label="Account Code" hint="Auto-generated from the account group">
            <input className="input font-mono bg-gray-100 font-semibold text-blue-700" value={form.accountCode} readOnly tabIndex={-1} />
          </FormField>

          <FormField label="Account Name" required error={errors.accountName} touched={touched.accountName || submitted}>
            <input className="input" value={form.accountName} onChange={e => setForm({ ...form, accountName: e.target.value })} onBlur={() => touch('accountName')} maxLength={100} placeholder="e.g. Current Assets" autoFocus />
          </FormField>

          <FormField label="Account Group Name">
            <select className="input" value={form.groupName}
              onChange={e => {
                const gn = e.target.value;
                // Auto-generate the code from the series for new entries (not when editing).
                setForm(f => ({ ...f, groupName: gn, ...(editing ? {} : { accountCode: nextCodeFor(gn) }) }));
              }}>
              {GROUP_NAMES.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </FormField>

          <FormField label="Parent Account" hint="Optional — for hierarchy">
            <select className="input" value={form.parentId} onChange={e => setForm({ ...form, parentId: e.target.value })}>
              <option value="">-- None (top level) --</option>
              {parentOptions.map(g => <option key={g.id} value={g.id}>{g.accountName}</option>)}
            </select>
          </FormField>

          <FormField label="Display Order" error={errors.displayOrder} touched={touched.displayOrder || submitted} hint="Lower numbers show first">
            <input className="input text-right" type="number" min="0" value={form.displayOrder} onChange={e => setForm({ ...form, displayOrder: e.target.value })} onBlur={() => touch('displayOrder')} />
          </FormField>
        </div>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button onClick={close} className="btn btn-secondary">Cancel</button>
          <button data-keyboard-save onClick={handleSave} className="btn btn-primary" disabled={submitted && hasErrors}>{editing ? 'Update' : 'Save'}</button>
        </div>
      </Modal>
    </div>
  );
}
